import * as React from 'react';
import { TypographySize } from 'mm-theme-configuration/dist/consts';
import { css, StyleSheet } from 'aphrodite/no-important';
import { getActiveEdition } from '../primaryHeader/Editions';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { useTheme } from '../../../../../theming/useTheme';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { getStyleBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { Link } from '../../../../partials/link/Link';
import { Directions, TriangleIcon } from '../TriangleIcon';
import { NavigationLink } from '../primaryHeader/PrimaryHeader';

interface EditionsProps {
    editions: NavigationLink[];
    editionEndpoint: string;
    verticalGapInLargeScreen: number;
    verticalGapInMediumScreen: number;
    verticalGapInSmallScreen: number;
    fontSizeSmall: TypographySize;
    fontSizeMedium: TypographySize;
    fontSizeLarge: TypographySize;
    colorTypeHover: string;
    colorTypeNormal: string;
    colorTypeActive: string;
    dropdownIconColor: string;
    editionsSeparatorColor: string;
    editionsMenuBackgroundColor: string;
}

const createStyles = ({
  verticalGapInSmallScreen,
  verticalGapInMediumScreen,
  verticalGapInLargeScreen,
  colorTypeActive,
  colorTypeNormal,
  colorTypeHover,
  editionsSeparatorColor,
  editionsMenuBackgroundColor }: EditionsProps, baseUnit: BaseUnit, editionsMenuToggle: boolean) => StyleSheet.create({
  wrapper: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.large]: {
      marginLeft: `${verticalGapInLargeScreen * baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginLeft: `${verticalGapInMediumScreen * baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginLeft: `${verticalGapInSmallScreen * baseUnit}px`,
    },
  },
  separator: {
    backgroundColor: editionsSeparatorColor,
    height: '50%',
    width: '1px',
    [MEDIA_BREAKPOINTS.large]: {
      marginRight: `${verticalGapInLargeScreen * baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginRight: `${verticalGapInMediumScreen * baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: 'none',
    },
  },
  button: {
    display: 'flex',
    color: colorTypeNormal,
    alignItems: 'center',
    cursor: 'pointer',
    ':hover': {
      color: colorTypeHover,
    },
    ':active': {
      color: colorTypeActive,
    },
  },
  link: {
    color: colorTypeNormal,
    ':hover': {
      color: colorTypeHover,
    },
    ':active': {
      color: colorTypeActive,
    },
  },
  ul: {
    backgroundColor: editionsMenuBackgroundColor,
    position: 'absolute',
    top: '100%',
    boxSizing: 'border-box',
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: editionsMenuToggle ? 'flex' : 'none',
    flexDirection: 'column',
    zIndex: 100,
    [MEDIA_BREAKPOINTS.large]: {
      width: `calc(${verticalGapInLargeScreen * baseUnit}px + 100%)`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      width: `calc(${verticalGapInMediumScreen * baseUnit}px + 100%)`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      width: `calc(${verticalGapInSmallScreen * baseUnit}px + 100%)`,
    },
  },
  li: {
    textAlign: 'center',
    margin: `${baseUnit}px 0`,
  },
});
export const Editions: React.FunctionComponent<EditionsProps> = props => {
  const { baseUnit, fontSizesStyles } = useTheme();
  const {
    editions,
    editionEndpoint,
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge,
    dropdownIconColor,
    colorTypeHover } = props;
  const [editionsMenuToggle, setEditionsMenuToggle] = React.useState(false);
  const styles = createStyles(props, baseUnit, editionsMenuToggle);
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  const fontStyle = getStyleBySize(fontSizesStyles, { fontSizeSmall, fontSizeMedium, fontSizeLarge });

  const scrollHandler = () => {
    if (editionsMenuToggle) {
      setEditionsMenuToggle(false);
    }
  };

  React.useEffect(() => {
    window.addEventListener('scroll', scrollHandler);

    return () => window.removeEventListener('scroll', scrollHandler);
  });

  const createEditionLink = (edition: NavigationLink) => (
    <li className={css(styles.li)}>
      <Link href={edition.link.href} className={css(styles.link, fontStyle)}>
        {edition.link.text}
      </Link>
    </li>
  );
  const EditionsMenu: React.FunctionComponent = () => (
    <ul className={css(styles.ul)}>
      {editions.map(createEditionLink)}
    </ul>
  );

  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.separator)} />
      <button type="button" className={css(styles.button, fontStyle)} onClick={() => setEditionsMenuToggle(!editionsMenuToggle)}>
        {activeEdition.link.text}
        <TriangleIcon direction={editionsMenuToggle ? Directions.UP : Directions.DOWN} color={dropdownIconColor} hoverColor={colorTypeHover} />
      </button>
      <EditionsMenu />
    </div>

  );
};
